import _ from 'lodash'
import notebookApi from '@/api/notebook'
import noteApi from '@/api/note'
import { isNullOrWhiteSpace } from '@/common/utils'

function emptyNotebook () {
  return {
    id: '',
    name: '',
    tags: [],
    createdTime: '',
    modifiedTime: '',
    published: false,
    publishedTime: '',
    catalogs: [],
    notes: [],
    version: ''
  }
}

function emptyNote () {
  return {
    id: '',
    name: '',
    tags: [],
    createdTime: '',
    modifiedTime: '',
    published: false,
    publishedTime: '',
    encryptionType: 0,
    content: '',
    lastContent: '',
    version: ''
  }
}

function emptySyncScroll () {
  return {
    items: [],
    preview: {
      position: {
        item: null,
        percent: 0
      }
    },
    code: {
      position: {
        item: null,
        percent: 0
      }
    }
  }
}

const state = {
  loading: {
    notebook: false,
    note: false
  },
  saveNoteContentInProcess: false,
  preview: false,
  type: 'NOTE',
  notebook: emptyNotebook(),
  note: emptyNote(),
  syncScroll: emptySyncScroll(),
  sidebar: {
    active: ''
  }
}

const getters = {
  preview (state) {
    return state.preview
  },
  isNotebook (state) {
    return state.type === 'NOTEBOOK'
  },
  isNote (state) {
    return state.type === 'NOTE'
  },
  notebook (state) {
    return state.notebook
  },
  note (state) {
    return state.note
  },
  syncScrollCodePosition (state) {
    return state.syncScroll.code.position
  },
  syncScrollItems (state) {
    return state.syncScroll.items
  },
  syncScrollPreviewPosition (state) {
    return state.syncScroll.preview.position
  },
  notebookCatalog (state) {
    let name = state.notebook.name
    if (/^\s*$/.test(name)) {
      name = '请输入笔记本名称'
    }

    const catalog = getCatalog({
      id: '',
      root: true
    }, state.notebook.catalogs, state.notebook.notes)
    const root = {
      id: state.notebook.id,
      name: name,
      empty: state.notebook.catalogs.length === 0 && state.notebook.notes.length === 0,
      root: true,
      items: catalog.items
    }
    return root
  },
  notebookLoading (state) {
    return state.loading.notebook
  },
  noteLoading (state) {
    return state.loading.note
  },
  catalogPaths (state) {
    return (catalogId) => {
      const paths = []
      const catalogs = state.notebook.catalogs
      let catalog
      while ((catalog = _.find(catalogs, n => n.id === catalogId))) {
        paths.push(catalog.name)
        catalogId = catalog.parentCatalogId
      }
      paths.push(state.notebook.name)
      _.reverse(paths)
      return paths
    }
  },
  isNoteContentChanged (state) {
    return state.note.content !== state.note.lastContent
  },
  getNotebookCatalog (state) {
    return (catalogId) => {
      return _.find(state.notebook.catalogs, n => n.id === catalogId)
    }
  },
  getNotebookNote (state) {
    return (noteId) => {
      return _.find(state.notebook.notes, n => n.id === noteId)
    }
  },
  saveNoteContentInProcess (state) {
    return state.saveNoteContentInProcess
  },
  catalogParents (state) {
    return (catalogId) => {
      if (catalogId === '') {
        return []
      }
      const parents = []
      const catalog = _.find(state.notebook.catalogs, n => n.id === catalogId)
      let parent = _.find(state.notebook.catalogs, n => n.id === catalog.parentCatalogId)
      while (parent) {
        parents.push(parent.id)
        parent = _.find(state.notebook.catalogs, n => n.id === parent.parentCatalogId)
      }
      return parents
    }
  },
  sidebarActive (state) {
    return state.sidebar.active
  }
}

const mutations = {
  SET_NOTEBOOK_NAME (state, name) {
    state.notebook.name = name
  },
  ADD_NOTEBOOK_TAG (state, tag) {
    state.notebook.tags.push(tag)
  },
  SET_NOTEBOOK_TAGS (state, tags) {
    state.notebook.tags = tags
  },
  SET_NOTE_NAME (state, name) {
    state.note.name = name
  },
  ADD_NOTE_TAG (state, tag) {
    state.note.tags.push(tag)
  },
  SET_NOTE_TAGS (state, tags) {
    state.note.tags = tags
  },
  SET_NOTE_CONTENT (state, content) {
    state.note.content = content
  },
  SET_PREVIEW (state, preview) {
    state.preview = preview
  },
  SET_SYNC_SCROLL_ITEMS (state, items) {
    state.syncScroll.items = items
  },
  SET_SYNC_SCROLL_CODE_POSITION (state, position) {
    state.syncScroll.code.position = position
  },
  SET_SYNC_SCROLL_PREVIEW_POSITION (state, position) {
    state.syncScroll.preview.position = position
  },
  SET_TYPE (state, type) {
    state.type = type
  },
  SET_NOTEBOOK (state, notebook) {
    state.notebook = notebook
  },
  SET_NOTE (state, note) {
    state.note = note
  },
  SET_SYNC_SCROLL (state, syncScroll) {
    state.syncScroll = syncScroll
  },
  SET_LOADING_NOTEBOOK (state, loading) {
    state.loading.notebook = loading
  },
  SET_LOADING_NOTE (state, loading) {
    state.loading.note = loading
  },
  ADD_NOTEBOOK_CATALOG (state, catalog) {
    state.notebook.catalogs.push(catalog)
  },
  UPDATE_NOTEBOOK_VERSION (state, data) {
    state.notebook.version = data.version
    state.notebook.modifiedTime = data.modifiedTime
  },
  ADD_NOTEBOOK_NOTE (state, note) {
    state.notebook.notes.push(note)
  },
  SET_NOTE_LAST_CONTENT (state, content) {
    state.note.lastContent = content
  },
  UPDATE_NOTE_VERSION (state, data) {
    state.note.version = data.version
    state.note.modifiedTime = data.modifiedTime
  },
  SET_NOTEBOOK_CATALOG_NAME (state, { catalogId, name }) {
    const catalog = _.find(state.notebook.catalogs, n => n.id === catalogId)
    if (catalog) {
      catalog.name = name
    }
  },
  SET_NOTEBOOK_NOTE_NAME (state, { noteId, name }) {
    const note = _.find(state.notebook.notes, n => n.id === noteId)
    if (note) {
      note.name = name
    }
  },
  SET_NOTEBOOK_CATALOGS (state, catalogs) {
    state.notebook.catalogs = catalogs
  },
  SET_NOTEBOOK_NOTES (state, notes) {
    state.notebook.notes = notes
  },
  SET_SAVE_NOTE_CONTENT_IN_PROCESS (state, value) {
    state.saveNoteContentInProcess = value
  },
  UPDATE_CATALOG_ORDER (state, { catalogId, order }) {
    const catalog = _.find(state.notebook.catalogs, n => n.id === catalogId)
    catalog.order = order
  },
  UPDATE_NOTE_ORDER (state, { noteId, order }) {
    const note = _.find(state.notebook.notes, n => n.id === noteId)
    note.order = order
  },
  UPDATE_CATALOG_PARENT_CATALOG (state, { catalogId, parentCatalogId, order }) {
    const catalog = _.find(state.notebook.catalogs, n => n.id === catalogId)
    catalog.order = order
    catalog.parentCatalogId = parentCatalogId
  },
  UPDATE_NOTE_CATALOG (state, { noteId, catalogId, order }) {
    const note = _.find(state.notebook.notes, n => n.id === noteId)
    note.order = order
    note.catalogId = catalogId
  },
  SET_SIDEBAR_ACTIVE (state, active) {
    state.sidebar.active = active
  }
}

const actions = {
  async loadNotebook ({ commit, dispatch }, { notebookId }) {
    commit('SET_NOTEBOOK', emptyNotebook())
    commit('SET_NOTE', emptyNote())
    commit('SET_LOADING_NOTEBOOK', true)
    commit('SET_TYPE', 'NOTEBOOK')

    try {
      const result = await notebookApi.get(notebookId)
      const notebook = {
        id: result.id,
        name: result.name,
        tags: result.tags,
        createdTime: result.createdTime,
        modifiedTime: result.modifiedTime,
        published: result.published,
        publishedTime: result.publishedTime,
        version: result.version,
        catalogs: _.map(result.catalogs, n => ({
          id: n.id,
          name: n.name,
          parentCatalogId: n.parentCatalogId,
          order: n.order
        })),
        notes: _.map(result.notes, n => ({
          id: n.id,
          catalogId: n.catalogId,
          name: n.name,
          order: n.order
        }))
      }
      commit('SET_NOTEBOOK', notebook)
    } finally {
      commit('SET_LOADING_NOTEBOOK', false)
    }
  },
  async addNotebookCatalog ({ state, commit }, { parentCatalogId, name }) {
    const catalogs = state.notebook.catalogs
    const siblingsCatalogs = _.filter(catalogs, n => n.parentCatalogId === parentCatalogId)
    const notes = state.notebook.notes
    const siblingsNotes = _.filter(notes, n => n.catalogId === parentCatalogId)
    const existOrders = _.sortBy([..._.map(siblingsCatalogs, n => n.order), ..._.map(siblingsNotes, n => n.order)], n => -n)
    let order = 1000000000000
    if (existOrders.length > 0) {
      order = existOrders[0] + 1000000000000
    }

    const result = await notebookApi.addCatalog({
      notebookId: state.notebook.id,
      parentCatalogId,
      name,
      order,
      lastVersion: state.notebook.version
    })
    commit('ADD_NOTEBOOK_CATALOG', {
      id: result.id,
      name: result.name,
      order,
      parentCatalogId
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
  },
  async addNotebookNote ({ state, commit }, { catalogId, name }) {
    const catalogs = state.notebook.catalogs
    const siblingsCatalogs = _.filter(catalogs, n => n.parentCatalogId === catalogId)
    const notes = state.notebook.notes
    const siblingsNotes = _.filter(notes, n => n.catalogId === catalogId)
    const existOrders = _.sortBy([..._.map(siblingsCatalogs, n => n.order), ..._.map(siblingsNotes, n => n.order)], n => -n)
    let order = 0
    if (existOrders.length > 0) {
      order = existOrders[0] + 1000000000000
    }

    const result = await notebookApi.addNote({
      notebookId: state.notebook.id,
      catalogId,
      name,
      order,
      lastVersion: state.notebook.version
    })
    commit('ADD_NOTEBOOK_NOTE', {
      id: result.id,
      name: result.name,
      order,
      catalogId
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    return result
  },
  async loadNotebookNote ({ state, commit, dispatch }, { notebookId, noteId }) {
    commit('SET_LOADING_NOTE', true)
    commit('SET_NOTE', emptyNote())
    commit('SET_SYNC_SCROLL', emptySyncScroll())
    commit('workbench/outline/RESET', null, { root: true })

    try {
      const result = await notebookApi.getNote({ notebookId, noteId })
      const note = {
        id: result.id,
        name: result.name,
        tags: result.tags,
        createdTime: result.createdTime,
        modifiedTime: result.modifiedTime,
        published: result.published,
        publishedTime: result.publishedTime,
        encryptionType: result.encryptionType,
        lastContent: result.content,
        content: result.content,
        version: result.version
      }
      commit('SET_NOTE', note)
      return result.content
    } finally {
      commit('SET_LOADING_NOTE', false)
    }
  },
  async loadNote ({ state, commit, dispatch }, { noteId }) {
    commit('SET_NOTEBOOK', emptyNotebook())
    commit('SET_NOTE', emptyNote())
    commit('SET_SYNC_SCROLL', emptySyncScroll())
    commit('SET_LOADING_NOTE', true)
    commit('SET_TYPE', 'NOTE')
    commit('workbench/outline/RESET', null, { root: true })

    try {
      const result = await noteApi.get(noteId)
      const note = {
        id: result.id,
        name: result.name,
        tags: result.tags,
        createdTime: result.createdTime,
        modifiedTime: result.modifiedTime,
        published: result.published,
        publishedTime: result.publishedTime,
        encryptionType: result.encryptionType,
        lastContent: result.content,
        content: result.content,
        version: result.version
      }
      commit('SET_NOTE', note)
      return result.content
    } finally {
      commit('SET_LOADING_NOTE', false)
    }
  },
  async changeNoteContent ({ state, getters, commit }) {
    if (!getters.isNoteContentChanged || getters.saveNoteContentInProcess) {
      return
    }
    try {
      commit('SET_SAVE_NOTE_CONTENT_IN_PROCESS', true)
      if (getters.isNotebook) {
        const result = await notebookApi.changeNoteContent({
          notebookId: state.notebook.id,
          noteId: state.note.id,
          content: state.note.content,
          lastVersion: state.notebook.version
        })
        commit('UPDATE_NOTEBOOK_VERSION', {
          modifiedTime: result.notebookModifiedTime,
          version: result.notebookVersion
        })
        commit('SET_NOTE_LAST_CONTENT', state.note.content)
        commit('UPDATE_NOTE_VERSION', {
          modifiedTime: result.notebookModifiedTime,
          version: 0
        })
      } else {
        const result = await noteApi.changeContent({
          id: state.note.id,
          content: state.note.content,
          lastVersion: state.note.version
        })
        commit('UPDATE_NOTE_VERSION', {
          modifiedTime: result.modifiedTime,
          version: result.version
        })
        commit('SET_NOTE_LAST_CONTENT', state.note.content)
      }
    } finally {
      commit('SET_SAVE_NOTE_CONTENT_IN_PROCESS', false)
    }
  },
  async addNotebookTag ({ state, commit }, tag) {
    console.log(tag)
    if (isNullOrWhiteSpace(tag)) {
      return
    }
    tag = _.trim(tag)
    if (_.find(state.notebook.tags, n => n.toLowerCase() === tag.toLowerCase())) {
      return
    }

    const tagNames = [...state.notebook.tags, tag]
    const result = await notebookApi.changeTags({
      id: state.notebook.id,
      tagNames,
      lastVersion: state.notebook.version
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.modifiedTime,
      version: result.version
    })
    commit('ADD_NOTEBOOK_TAG', tag)
  },
  async addNoteTag ({ state, getters, commit }, tag) {
    if (isNullOrWhiteSpace(tag)) {
      return
    }
    tag = _.trim(tag)
    if (_.find(state.note.tags, n => n.toLowerCase() === tag.toLowerCase())) {
      return
    }
    const tagNames = [...state.note.tags, tag]
    if (getters.isNotebook) {
      const result = await notebookApi.changeNoteTags({
        notebookId: state.notebook.id,
        noteId: state.note.id,
        lastVersion: state.notebook.version,
        tagNames
      })
      commit('UPDATE_NOTEBOOK_VERSION', {
        modifiedTime: result.notebookModifiedTime,
        version: result.notebookVersion
      })
      commit('UPDATE_NOTE_VERSION', {
        modifiedTime: result.notebookModifiedTime,
        version: 0
      })
    } else {
      const result = await noteApi.changeTags({
        id: state.note.id,
        lastVersion: state.note.version,
        tagNames
      })
      commit('UPDATE_NOTE_VERSION', {
        modifiedTime: result.modifiedTime,
        version: result.version
      })
    }
    commit('ADD_NOTE_TAG', tag)
  },
  async removeNotebookTag ({ state, commit }, tag) {
    const tagNames = [...state.notebook.tags]
    _.remove(tagNames, n => n === tag)

    const result = await notebookApi.changeTags({
      id: state.notebook.id,
      tagNames,
      lastVersion: state.notebook.version
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.modifiedTime,
      version: result.version
    })
    commit('SET_NOTEBOOK_TAGS', tagNames)
  },
  async removeNoteTag ({ state, getters, commit }, tag) {
    const tagNames = [...state.note.tags]
    _.remove(tagNames, n => n === tag)
    if (getters.isNotebook) {
      const result = await notebookApi.changeNoteTags({
        notebookId: state.notebook.id,
        noteId: state.note.id,
        lastVersion: state.notebook.version,
        tagNames
      })
      commit('UPDATE_NOTEBOOK_VERSION', {
        modifiedTime: result.notebookModifiedTime,
        version: result.notebookVersion
      })
      commit('UPDATE_NOTE_VERSION', {
        modifiedTime: result.notebookModifiedTime,
        version: 0
      })
    } else {
      const result = await noteApi.changeTags({
        id: state.note.id,
        lastVersion: state.note.version,
        tagNames
      })
      commit('UPDATE_NOTE_VERSION', {
        modifiedTime: result.modifiedTime,
        version: result.version
      })
    }
    commit('SET_NOTE_TAGS', tagNames)
  },
  async renameNotebook ({ state, commit, dispatch }, name) {
    const result = await notebookApi.rename({
      id: state.notebook.id,
      name,
      lastVersion: state.notebook.version
    })
    commit('SET_NOTEBOOK_NAME', name)
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.modifiedTime,
      version: result.version
    })
    dispatch('workbench/opened/changeName', {
      notebookId: state.notebook.id,
      type: 'NOTEBOOK',
      notebookName: name
    }, { root: true })
  },
  async renameNotebookCatalog ({ state, commit, dispatch }, { catalogId, name }) {
    const result = await notebookApi.renameCatalog({
      notebookId: state.notebook.id,
      catalogId,
      name,
      lastVersion: state.notebook.version
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('SET_NOTEBOOK_CATALOG_NAME', {
      catalogId,
      name
    })
  },
  async renameNotebookNote ({ state, commit, dispatch }, { noteId, name }) {
    const result = await notebookApi.renameNote({
      notebookId: state.notebook.id,
      noteId,
      name,
      lastVersion: state.notebook.version
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('SET_NOTEBOOK_NOTE_NAME', {
      noteId,
      name
    })
    if (state.note.id === noteId) {
      commit('SET_NOTE_NAME', name)
      commit('UPDATE_NOTE_VERSION', {
        modifiedTime: result.notebookModifiedTime,
        version: 0
      })
    }
    dispatch('workbench/opened/changeName', {
      noteId: noteId,
      type: 'NOTE',
      noteName: name
    }, { root: true })
  },
  async renameNote ({ state, commit, dispatch }, name) {
    const result = await noteApi.rename({
      id: state.note.id,
      name,
      lastVersion: state.note.version
    })
    commit('SET_NOTE_NAME', name)
    commit('UPDATE_NOTE_VERSION', {
      modifiedTime: result.modifiedTime,
      version: result.version
    })
    dispatch('workbench/opened/changeName', {
      noteId: state.note.id,
      type: 'NOTE',
      noteName: name
    }, { root: true })
  },
  async removeNotebookCatalog ({ state, commit }, catalogId) {
    const result = await notebookApi.removeCatalog({
      notebookId: state.notebook.id,
      catalogId,
      lastVersion: state.notebook.version
    })

    const removedCatalogIds = [catalogId]
    let i = 0
    while (i < removedCatalogIds.length) {
      const currentCatalogId = removedCatalogIds[i]
      _.forEach(_.filter(state.notebook.catalogs, n => n.parentCatalogId === currentCatalogId), n => {
        removedCatalogIds.push(n.id)
      })
      i++
    }
    const catalogs = [...state.notebook.catalogs]
    _.remove(catalogs, n => _.includes(removedCatalogIds, n.id))

    const notes = [...state.notebook.notes]
    _.remove(notes, n => _.includes(removedCatalogIds, n.catalogId))

    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('SET_NOTEBOOK_CATALOGS', catalogs)
    commit('SET_NOTEBOOK_NOTES', notes)

    let reloadNotebook = false
    if (state.note.id !== '' && _.find(state.notebook.notes, n => n.id === state.note.id) === undefined) {
      reloadNotebook = true
      commit('SET_NOTE', emptyNote())
    }
    return reloadNotebook
  },
  async removeNotebookNote ({ state, commit }, noteId) {
    const result = await notebookApi.removeNote({
      notebookId: state.notebook.id,
      noteId,
      lastVersion: state.notebook.version
    })

    const notes = [...state.notebook.notes]
    _.remove(notes, n => n.id === noteId)

    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('SET_NOTEBOOK_NOTES', notes)

    let reloadNotebook = false
    if (state.note.id === noteId) {
      reloadNotebook = true
      commit('SET_NOTE', emptyNote())
    }
    return reloadNotebook
  },
  async upCatalogOrder ({ state, commit }, catalogId) {
    const newOrder = catalogNewOrder(state, catalogId, true)
    if (newOrder.stay) {
      return
    }

    const result = await notebookApi.changeCatalogOrder({
      notebookId: state.notebook.id,
      catalogId,
      lastVersion: state.notebook.version,
      order: newOrder.order
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('UPDATE_CATALOG_ORDER', {
      catalogId,
      order: newOrder.order
    })
  },
  async downCatalogOrder ({ state, commit }, catalogId) {
    const newOrder = catalogNewOrder(state, catalogId, false)
    if (newOrder.stay) {
      return
    }

    const result = await notebookApi.changeCatalogOrder({
      notebookId: state.notebook.id,
      catalogId,
      lastVersion: state.notebook.version,
      order: newOrder.order
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('UPDATE_CATALOG_ORDER', {
      catalogId,
      order: newOrder.order
    })
  },
  async upNoteOrder ({ state, commit }, noteId) {
    const newOrder = noteNewOrder(state, noteId, true)
    if (newOrder.stay) {
      return
    }

    const result = await notebookApi.changeNoteOrder({
      notebookId: state.notebook.id,
      noteId,
      lastVersion: state.notebook.version,
      order: newOrder.order
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('UPDATE_NOTE_ORDER', {
      noteId,
      order: newOrder.order
    })
  },
  async downNoteOrder ({ state, commit }, noteId) {
    const newOrder = noteNewOrder(state, noteId, false)
    if (newOrder.stay) {
      return
    }

    const result = await notebookApi.changeNoteOrder({
      notebookId: state.notebook.id,
      noteId,
      lastVersion: state.notebook.version,
      order: newOrder.order
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('UPDATE_NOTE_ORDER', {
      noteId,
      order: newOrder.order
    })
  },
  async changeCatalogParentCatalog ({ state, commit }, { catalogId, parentCatalogId }) {
    const catalog = _.find(state.notebook.catalogs, n => n.id === catalogId)
    if (catalog.parentCatalogId === parentCatalogId) {
      return
    }
    const catalogParents = getters.catalogParents(parentCatalogId)
    if (_.includes(catalogParents, catalog.parentCatalogId)) {
      console.warn('不允许将父目录粘贴到其子目录内')
      return
    }
    const newOrder = newItemOrder(state, parentCatalogId)
    const result = await notebookApi.changeCatalogParentCatalog({
      notebookId: state.notebook.id,
      catalogId,
      parentCatalogId,
      order: newOrder,
      lastVersion: state.notebook.version
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('UPDATE_CATALOG_PARENT_CATALOG', {
      catalogId,
      parentCatalogId,
      order: newOrder
    })
  },
  async changeNoteCatalog ({ state, getters, commit }, { noteId, catalogId }) {
    const note = _.find(state.notebook.notes, n => n.id === noteId)
    if (note.catalogId === catalogId) {
      return
    }

    const newOrder = newItemOrder(state, catalogId)
    const result = await notebookApi.changeNoteCatalog({
      notebookId: state.notebook.id,
      noteId,
      catalogId,
      order: newOrder,
      lastVersion: state.notebook.version
    })
    commit('UPDATE_NOTEBOOK_VERSION', {
      modifiedTime: result.notebookModifiedTime,
      version: result.notebookVersion
    })
    commit('UPDATE_NOTE_CATALOG', {
      noteId,
      catalogId,
      order: newOrder
    })
  }

}

function getCatalog (catalog, catalogs, notes) {
  const items = _.map(_.filter(notes, n => n.catalogId === catalog.id), n => {
    return {
      type: 'NOTE',
      id: n.id,
      name: n.name,
      parentId: catalog.id,
      order: n.order
    }
  })

  const children = _.filter(catalogs, n => n.parentCatalogId === catalog.id)
  _.forEach(children, n => {
    items.push(getCatalog(n, catalogs, notes))
  })

  if (!catalog.root) {
    return {
      type: 'CATALOG',
      id: catalog.id,
      name: catalog.name,
      parentId: catalog.parentCatalogId,
      items: _.sortBy(items, n => n.order),
      order: catalog.order,
      empty: items.length === 0
    }
  } else {
    return {
      items: _.sortBy(items, n => n.order)
    }
  }
}

function catalogNewOrder (state, catalogId, up) {
  const catalog = _.find(state.notebook.catalogs, n => n.id === catalogId)

  const siblingsCatalogs = _.map(_.filter(state.notebook.catalogs, n => n.parentCatalogId === catalog.parentCatalogId), n => ({
    id: n.id,
    order: n.order
  }))
  const siblingsNotes = _.map(_.filter(state.notebook.notes, n => n.catalogId === catalog.parentCatalogId), n => ({
    id: n.id,
    order: n.order
  }))
  const siblings = _.sortBy([...siblingsNotes, ...siblingsCatalogs], n => n.order)
  const index = _.findIndex(siblings, n => n.id === catalogId)

  if (up) {
    if (index === 0) {
      return {
        stay: true
      }
    }

    let order
    if (index === 1) {
      order = siblings[0].order - Math.floor((siblings[0].order - 0) / 2)
    } else {
      order = siblings[index - 1].order - Math.floor((siblings[index - 1].order - siblings[index - 2].order) / 2)
    }

    return {
      stay: false,
      order
    }
  }

  if (index === siblings.length - 1) {
    return {
      stay: true
    }
  }

  let order
  if (index === siblings.length - 2) {
    order = siblings[siblings.length - 1].order + 1000000000000
  } else {
    order = siblings[index + 1].order + Math.floor((siblings[index + 2].order - siblings[index + 1].order) / 2)
  }

  return {
    stay: false,
    order
  }
}

function noteNewOrder (state, noteId, up) {
  const note = _.find(state.notebook.notes, n => n.id === noteId)

  const siblingsCatalogs = _.map(_.filter(state.notebook.catalogs, n => n.parentCatalogId === note.catalogId), n => ({
    id: n.id,
    order: n.order
  }))
  const siblingsNotes = _.map(_.filter(state.notebook.notes, n => n.catalogId === note.catalogId), n => ({
    id: n.id,
    order: n.order
  }))
  const siblings = _.sortBy([...siblingsNotes, ...siblingsCatalogs], n => n.order)
  const index = _.findIndex(siblings, n => n.id === noteId)

  if (up) {
    if (index === 0) {
      return {
        stay: true
      }
    }

    let order
    if (index === 1) {
      order = siblings[0].order - Math.floor((siblings[0].order - 0) / 2)
    } else {
      order = siblings[index - 1].order - Math.floor((siblings[index - 1].order - siblings[index - 2].order) / 2)
    }

    return {
      stay: false,
      order
    }
  }

  if (index === siblings.length - 1) {
    return {
      stay: true
    }
  }

  let order
  if (index === siblings.length - 2) {
    order = siblings[siblings.length - 1].order + 1000000000000
  } else {
    order = siblings[index + 1].order + Math.floor((siblings[index + 2].order - siblings[index + 1].order) / 2)
  }

  return {
    stay: false,
    order
  }
}

function newItemOrder (state, catalogId) {
  const siblingsCatalogs = _.map(_.filter(state.notebook.catalogs, n => n.parentCatalogId === catalogId), n => ({
    id: n.id,
    order: n.order
  }))
  const siblingsNotes = _.map(_.filter(state.notebook.notes, n => n.catalogId === catalogId), n => ({
    id: n.id,
    order: n.order
  }))
  const siblings = _.sortBy([...siblingsNotes, ...siblingsCatalogs], n => n.order)
  if (siblings.length === 0) {
    return 1000000000000
  }
  return siblings[siblings.length - 1].order + 1000000000000
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
